import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { submitSurveillanceRouteEdit } from '../gql/services/surveillanceRouteEdit';
import { buildAddRoadToRouteInput } from '../utils/surveillanceRoute';
import { showSupportRoadAddedToast } from '../utils/toasts';
import { useRoadNamesById } from './useRoadDirectionLabels';

type RouteForAdd = Parameters<typeof buildAddRoadToRouteInput>[0];

/** Rastro del añadido automático, SOLO en desarrollo (ver `trazaApoyo` del detector). */
const traceSupportRoad = (step: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.log(`[support-road] ${step}`, data ?? '');
  }
};

/**
 * Añade SOLA la vía de apoyo al recorrido y avisa (2026-09-29).
 *
 * Probado en el navegador, el aviso rojo «estás en la C-25z, que no es de este
 * recorrido» + «Añadir» no servía: solo se pinta en la pestaña Vista previa, y
 * un operario conduciendo no mira una línea roja. Decisión del usuario: si la
 * patrulla se ha cambiado de carretera y sigue por ella, es que se ha cambiado
 * —se añade sin preguntar y se dice con un toast—.
 *
 * El CUÁNDO lo decide el detector (`supportRoadToAdopt`: lecturas buenas
 * seguidas en la vía). Esto hace la MISMA edición que el botón «Añadir», que
 * sin cobertura se encola y la caché ya la refleja al momento.
 *
 * Una sola vez por recorrido y vía: el detector la sigue pidiendo hasta que la
 * vía entra en el recorrido, y si el guardado falla no se reintenta en bucle
 * —queda el botón «Añadir» del aviso, y el cierre del tramo la añade igual—.
 */
export const useAutoAddSupportRoad = ({
  route,
  roadId,
  enabled,
  onAdded,
}: {
  /** El recorrido que graba esta tablet. */
  route: RouteForAdd | null | undefined;
  /** La vía de apoyo que pide el detector, o `null`. */
  roadId: string | null;
  /** ¿Puede esta cuenta editar el recorrido? (la regla del cronómetro) */
  enabled: boolean;
  /** Tras añadirla: p. ej. que la pantalla vuelva a leer el recorrido. */
  onAdded?: () => void;
}): { failedRoadId: string | null } => {
  const client = useApolloClient();
  /**
   * La vía que NO se pudo añadir. Solo entonces vuelve el aviso rojo con
   * «Añadir» de la ficha: si se añade sola, esa frase sobra (el usuario,
   * 2026-09-29); si falla, es la salida a mano.
   */
  const [failedRoadId, setFailedRoadId] = useState<string | null>(null);
  const names = useRoadNamesById(roadId ? [roadId] : []);
  const attemptedRef = useRef(new Set<string>());

  // Latest-refs: el efecto solo se relanza por lo que decide si añadir, no
  // por un nombre que llega tarde o un callback nuevo en cada render.
  const routeRef = useRef(route);
  routeRef.current = route;
  const namesRef = useRef(names);
  namesRef.current = names;
  const onAddedRef = useRef(onAdded);
  onAddedRef.current = onAdded;

  const routeId = route?.id;
  const alreadyInRoute = !!roadId && !!route?.roadIDs?.includes(roadId);

  useEffect(() => {
    const current = routeRef.current;
    if (roadId) {
      traceSupportRoad('decidir', {
        roadId,
        enabled,
        routeId: current?.id,
        alreadyInRoute,
        alreadyAttempted: attemptedRef.current.has(`${current?.id}:${roadId}`),
      });
    }
    if (!enabled || !roadId || !current?.id || alreadyInRoute) return;
    const key = `${current.id}:${roadId}`;
    if (attemptedRef.current.has(key)) return;
    attemptedRef.current.add(key);

    submitSurveillanceRouteEdit(
      client,
      buildAddRoadToRouteInput(current, roadId, crypto.randomUUID())
    )
      .then((outcome) => {
        traceSupportRoad('AÑADIDA', { roadId, outcome });
        // Guardada o encolada, en la tablet ya surte efecto: se avisa igual.
        showSupportRoadAddedToast(namesRef.current[roadId] ?? '');
        onAddedRef.current?.();
      })
      .catch((err) => {
        traceSupportRoad('el guardado FALLA', err);
        setFailedRoadId(roadId);
        Sentry.captureException(err, {
          tags: { feature: 'surveillance-route', hook: 'useAutoAddSupportRoad' },
        });
      });
  }, [enabled, roadId, routeId, alreadyInRoute, client]);

  return { failedRoadId };
};

export default useAutoAddSupportRoad;
